/**
 * SquadLeaderQuickClock.jsx — lets a squad LEADER (regular worker) clock
 * their whole squad in/out at once, mirroring the supervisor TeamQuickClock.
 * Renders nothing unless the user leads at least one squad.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../hooks/use-toast';
import { Users, Clock, LogOut, Loader2, Crown } from 'lucide-react';
import axios from 'axios';

const API = `${window.__BACKEND_URL__}/api`;

export const SquadLeaderQuickClock = () => {
  const { toast } = useToast();
  const [squads, setSquads] = useState([]);
  const [squadId, setSquadId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/my/led-squads`);
      const list = data.squads || [];
      setSquads(list);
      setSquadId((prev) => prev || (list[0] && list[0].id) || '');
    } catch (e) { /* silent — widget simply hides */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const squad = squads.find((s) => s.id === squadId);
  if (!squad) return null;

  const clockedIn = squad.members.filter((m) => m.is_clocked_in).length;
  const total = squad.members.length;

  const act = async (kind) => {
    setBusy(true);
    try {
      const url = kind === 'in' ? `${API}/teams/clock-in` : `${API}/teams/clock-out`;
      const body = kind === 'in'
        ? { team_id: squad.id, send_notifications: true }
        : { team_id: squad.id };
      const { data } = await axios.post(url, body);
      toast({
        title: `${squad.name} Clocked ${kind === 'in' ? 'In' : 'Out'}`,
        description: `${kind === 'in' ? data.clocked_in : data.clocked_out} member(s) clocked ${kind}`,
      });
      load();
    } catch (e) {
      toast({
        title: `Squad Clock ${kind === 'in' ? 'In' : 'Out'} Failed`,
        description: e.response?.data?.detail || 'Something went wrong',
        variant: 'destructive',
      });
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-xl border border-amber-300/50 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-900/10 p-4 flex flex-wrap items-center justify-between gap-3"
      data-testid="squad-leader-quick-clock"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-amber-500/15">
          <Crown className="w-5 h-5 text-amber-500" />
        </div>
        <div className="min-w-0">
          {squads.length > 1 ? (
            <select
              value={squadId}
              onChange={(e) => setSquadId(e.target.value)}
              className="max-w-[220px] rounded-lg px-2 py-1 text-sm font-semibold bg-transparent border border-amber-300/50 dark:border-amber-500/30 cursor-pointer"
              data-testid="squad-leader-squad-select"
            >
              {squads.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          ) : (
            <p className="text-sm font-semibold truncate">{squad.name}</p>
          )}
          <p className="text-xs mt-0.5 text-slate-500" data-testid="squad-leader-count">
            You lead this squad · <span className={clockedIn > 0 ? 'text-emerald-500' : ''}>{clockedIn}</span>/{total} on the clock
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => act('in')}
          disabled={busy || (total > 0 && clockedIn === total)}
          className="px-4 py-2 rounded-xl text-xs font-semibold text-white disabled:opacity-40"
          style={{ background: '#00C896' }}
          data-testid="squad-leader-clock-in-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : <Clock className="w-3.5 h-3.5 inline mr-1" />} Clock In Squad
        </button>
        <button
          onClick={() => act('out')}
          disabled={busy || clockedIn === 0}
          className="px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-40 text-red-500 border border-red-400/40"
          data-testid="squad-leader-clock-out-btn"
        >
          <LogOut className="w-3.5 h-3.5 inline mr-1" /> Clock Out Squad
        </button>
      </div>
    </div>
  );
};

export default SquadLeaderQuickClock;
